import React, { useMemo } from "react";
import { Circle, Line, Group, Text } from "react-konva";
import { HAND_IDS } from "@/entities/annotation/model/consts";
import { useAppSelector } from "@/app/appStore";
import { selectHandProjections } from "@/entities/annotation/model/slices/slice";
import { findClosestHandProjection } from "../lib/helpers";
import { CAMERA_FRAME_IMAGE_SIZE } from "@/shared/consts";
import { useMediaState } from "@tezzro/player";

const HAND_COLORS = {
  RIGHT: {
    SHOULDER: "#840000",
    ELBOW: "#AE0501",
    WRIST: "#FF0000",
  },
  LEFT: {
    SHOULDER: "#0E3F85",
    ELBOW: "#1857AF",
    WRIST: "#3B82F8",
  },
  STROKE: "#FFFFFF",
} as const;

export type HandProjectionPoint = {
  x: number;
  y: number;
  confidence: number;
};

export type HandProjectionDisplay = {
  shoulder: HandProjectionPoint;
  elbow: HandProjectionPoint;
  wrist: HandProjectionPoint;
  timestamp: string;
  gondolaDistance?: number;
};

const HandJoint: React.FC<{
  x: number;
  y: number;
  radius: number;
  fill: string;
  confidence: number;
}> = ({ x, y, radius, fill, confidence }) => (
  <Circle
    x={x}
    y={y}
    radius={radius}
    fill={fill}
    stroke={HAND_COLORS.STROKE}
    strokeWidth={2}
    opacity={confidence}
  />
);

const HandConnection: React.FC<{
  startX: number;
  startY: number;
  endX: number;
  endY: number;
}> = ({ startX, startY, endX, endY }) => (
  <Line
    points={[startX, startY, endX, endY]}
    stroke={HAND_COLORS.STROKE}
    strokeWidth={2}
    opacity={0.8}
    lineCap="round"
    lineJoin="round"
  />
);

const GondolaDistanceDisplay: React.FC<{
  wristX: number;
  wristY: number;
  distance: number;
}> = ({ wristX, wristY, distance }) => (
  <>
    <Circle
      x={wristX + 25}
      y={wristY - 10}
      radius={18}
      fill="#000000"
      opacity={0.8}
    />
    <Text
      x={wristX + 7}
      y={wristY - 14}
      text={`${Math.trunc(distance * 100)}cm`}
      fontSize={10}
      fontStyle="bold"
      fill="#00FF00"
      align="center"
      width={36}
      opacity={1}
    />
  </>
);

export const HandProjections: React.FC = () => {
  const handProjections = useAppSelector(selectHandProjections);
  const currentTime = useMediaState("currentTime");
  const canvasDimensions = useAppSelector(
    (state) => state.annotation.canvasDimensions
  );

  const activeProjection = useMemo(() => {
    if (!handProjections?.projections) {
      return null;
    }
    return findClosestHandProjection(handProjections.projections, currentTime);
  }, [handProjections?.projections, currentTime]);

  const colors = useMemo(
    () =>
      handProjections?.handId === HAND_IDS.LEFT
        ? HAND_COLORS.LEFT
        : HAND_COLORS.RIGHT,
    [handProjections?.handId]
  );

  const projectionData = useMemo(() => {
    if (!activeProjection?.projection) {
      return null;
    }

    const [shoulder, elbow, wrist] = activeProjection.projection;

    const scaleX = canvasDimensions.width / CAMERA_FRAME_IMAGE_SIZE.width;
    const scaleY = canvasDimensions.height / CAMERA_FRAME_IMAGE_SIZE.height;

    return {
      shoulder: {
        x: shoulder[0] * scaleX,
        y: shoulder[1] * scaleY,
        confidence: shoulder[2],
      },
      elbow: {
        x: elbow[0] * scaleX,
        y: elbow[1] * scaleY,
        confidence: elbow[2],
      },
      wrist: {
        x: wrist[0] * scaleX,
        y: wrist[1] * scaleY,
        confidence: wrist[2],
      },
      gondolaDistance: activeProjection.gondola_distance,
    };
  }, [activeProjection, canvasDimensions]);

  if (!projectionData) {
    return null;
  }

  const { shoulder, elbow, wrist, gondolaDistance } = projectionData;

  return (
    <Group>
      <HandConnection
        startX={shoulder.x}
        startY={shoulder.y}
        endX={elbow.x}
        endY={elbow.y}
      />
      <HandConnection
        startX={elbow.x}
        startY={elbow.y}
        endX={wrist.x}
        endY={wrist.y}
      />

      <HandJoint
        x={shoulder.x}
        y={shoulder.y}
        radius={6}
        fill={colors.SHOULDER}
        confidence={shoulder.confidence}
      />
      <HandJoint
        x={elbow.x}
        y={elbow.y}
        radius={5}
        fill={colors.ELBOW}
        confidence={elbow.confidence}
      />
      <HandJoint
        x={wrist.x}
        y={wrist.y}
        radius={4}
        fill={colors.WRIST}
        confidence={wrist.confidence}
      />

      {gondolaDistance && (
        <GondolaDistanceDisplay
          wristX={wrist.x}
          wristY={wrist.y}
          distance={gondolaDistance}
        />
      )}
    </Group>
  );
};
